/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component — the privacy information line of the public booking
 *              consents, tenant first: «{tenant} processes your data…» + the
 *              tenant's privacy policy (and terms) link, then the processor
 *              line «Beebeeboard … on behalf of {tenant}» with Beebeeboard's
 *              own privacy link. Informational only: no tick, no payload.
 *   extends:   Component (@glimmer/component)
 *   template:  notice.hbs
 *   args:      @context ('booking' default | 'checkin': which purpose the
 *              sentence names), @showTerms (default true: the terms link;
 *              false where a tick row already links the terms)
 *   uses:      service:session (openOrganization.name, option_field),
 *              lib/consent-links (tenantPrivacyUrl, tenantTermsUrl,
 *              BEEBEEBOARD_* fallbacks)
 *   siblings:  booking-consent/option.hbs (the tick rows),
 *              booking-index/booking-summary.hbs, templates/checkin.hbs,
 *              checkin-nuova/consents.hbs, templates/index.hbs (callers)
 *   tests:     booking-consent-test.js
 *   gotchas:   no tenant privacy URL configured → the link falls back to the
 *              Beebeeboard privacy page (owner rule 2026-10-21) and the
 *              processor line drops its own link (same page twice otherwise);
 *              the wording stays tenant-first.
 */
import Component from '@glimmer/component';
import { inject as iService } from '@ember/service';
import {
  BEEBEEBOARD_PRIVACY_URL,
  BEEBEEBOARD_TERMS_URL,
  tenantPrivacyUrl,
  tenantTermsUrl,
} from 'beebeeboard-booking/lib/consent-links';

export default class BookingConsentNoticeComponent extends Component {
  @iService session;

  get tenantName() {
    const name = this.session?.openOrganization?.name;
    return typeof name === 'string' && name.trim() ? name.trim() : null;
  }

  get context() {
    return this.args.context === 'checkin' ? 'checkin' : 'booking';
  }

  get showTerms() {
    return this.args.showTerms !== false;
  }

  get tenantPrivacyUrl() {
    return tenantPrivacyUrl(this.session?.option_field);
  }

  get privacyUrl() {
    return this.tenantPrivacyUrl || BEEBEEBOARD_PRIVACY_URL;
  }

  get termsUrl() {
    return tenantTermsUrl(this.session?.option_field) || BEEBEEBOARD_TERMS_URL;
  }

  // The processor line links Beebeeboard's own policy only when the first link is the tenant's.
  get showProcessorLink() {
    return Boolean(this.tenantPrivacyUrl);
  }

  get processorPrivacyUrl() {
    return BEEBEEBOARD_PRIVACY_URL;
  }
}
